/**
 * Meta Cloud API — Facebook SDK Configuration
 *
 * Uses FB.login() popup (Embedded Signup) instead of redirect-based OAuth.
 * The config_id comes from Meta's WhatsApp Embedded Signup configuration.
 *
 * META_APP_ID and META_CONFIG_ID are defined per environment
 * in the vite.config.{local|dev|prod}.ts `define` blocks,
 * following the same pattern as URL_API_PLICK, FIREBASE_API_KEY, etc.
 *
 * Coexistence: el negocio conecta el número que YA usa en la app de WhatsApp
 * Business. Se activa pasando `extras.featureType = 'whatsapp_business_app_onboarding'`.
 * En ese flujo el popup reemplaza la selección de WABA por una pantalla de "conectar
 * cuenta existente" (código de verificación en la app) y el postMessage de éxito
 * (`event: FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING`) trae SOLO `waba_id` — el
 * `phone_number_id` lo resuelve el backend con GET /{waba_id}/phone_numbers.
 *
 * See: https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/onboarding-business-app-users
 */

/* eslint-disable no-var */
declare const META_APP_ID: string;
declare const META_CONFIG_ID: string;
/* eslint-enable no-var */

const META_SDK_VERSION = 'v25.0';

/**
 * featureType que activa el flujo Coexistence del Embedded Signup.
 * Si se quisiera revertir al onboarding Cloud API estándar, basta con no pasar `extras`.
 */
export const COEXISTENCE_FEATURE_TYPE = 'whatsapp_business_app_onboarding';

/** Resultado del Embedded Signup: el code más los identificadores capturados del postMessage. */
export interface EmbeddedSignupResult {
  code: string;
  wabaId?: string;
  phoneNumberId?: string;
  featureType: string;
  finishEvent?: string;
}

/** Estructura del `message` event que Meta emite durante el Embedded Signup. */
interface WaEmbeddedSignupMessage {
  type: 'WA_EMBEDDED_SIGNUP';
  event?: string;
  version?: number;
  data?: {
    waba_id?: string;
    phone_number_id?: string;
    business_id?: string;
    current_step?: string;
    error_message?: string;
  };
}

/**
 * Load the Facebook SDK asynchronously.
 * Idempotent: if already loaded, resolves immediately.
 * @returns {Promise<void>} Resolves when SDK is ready
 */
export const loadFacebookSdk = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (window.FB) {
      resolve();
      return;
    }

    // Init callback
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: false,
        version: META_SDK_VERSION,
      });
      resolve();
    };

    // Inject SDK script if not already present
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
      document.head.appendChild(script);
    }
  });
};

/**
 * Launch the Meta Embedded Signup popup via FB.login() en modo Coexistence.
 *
 * Captura el `waba_id` (y `phone_number_id` si el flujo lo provee) vía el
 * `message` event de Meta y lo combina con el auth code del callback de FB.login.
 *
 * @returns {Promise<EmbeddedSignupResult | null>} Resultado o null si el usuario cancela.
 */
export const launchEmbeddedSignup = (): Promise<EmbeddedSignupResult | null> => {
  return new Promise((resolve, reject) => {
    if (!window.FB) {
      reject(new Error('Facebook SDK not loaded'));
      return;
    }

    // El postMessage de Coexistence llega ANTES del callback de FB.login y trae solo
    // `waba_id`; lo guardamos para fusionarlo con el `code` cuando resuelva el login.
    let sessionInfo: { wabaId?: string; phoneNumberId?: string; finishEvent?: string } = {};

    const sessionInfoListener = (event: MessageEvent) => {
      if (!event.origin.endsWith('facebook.com')) return;
      try {
        const message = JSON.parse(event.data) as WaEmbeddedSignupMessage;
        if (message.type !== 'WA_EMBEDDED_SIGNUP') return;
        sessionInfo = {
          wabaId: message.data?.waba_id,
          phoneNumberId: message.data?.phone_number_id,
          finishEvent: message.event,
        };
      } catch {
        // Mensaje no-JSON o ajeno al Embedded Signup — ignorar.
      }
    };

    window.addEventListener('message', sessionInfoListener);
    const cleanup = () => window.removeEventListener('message', sessionInfoListener);

    const loginOptions = {
      config_id: META_CONFIG_ID,
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: COEXISTENCE_FEATURE_TYPE,
      },
    };

    window.FB.login((response: { authResponse?: { code?: string } }) => {
      cleanup();
      const code = response.authResponse?.code;
      if (code) {
        resolve({
          code,
          wabaId: sessionInfo.wabaId,
          phoneNumberId: sessionInfo.phoneNumberId,
          featureType: COEXISTENCE_FEATURE_TYPE,
          finishEvent: sessionInfo.finishEvent,
        });
      } else {
        // User cancelled or closed the popup
        resolve(null);
      }
    }, loginOptions);
  });
};

// Extend window for Facebook SDK types
declare global {
  interface Window {
    FB: {
      init: (params: { appId: string; cookie: boolean; xfbml: boolean; version: string }) => void;
      login: (
        callback: (response: { authResponse?: { code?: string } }) => void,
        options: {
          config_id: string;
          response_type: string;
          override_default_response_type: boolean;
          extras?: {
            setup?: Record<string, unknown>;
            featureType?: string;
            sessionInfoVersion?: string;
          };
        },
      ) => void;
    };
    fbAsyncInit: () => void;
  }
}
